
import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  AlertComponent,
  ButtonComponent,
  ChipComponent,
  DropdownMenuComponent,
  GridModule,
  GridNoRowsComponent,
  LoaderModule,
} from '@mds/ui';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { ComposerApiService } from '@services/composer-api.service';
import { PefScoreComponent } from './component/pef-score/pef-score.component';
import { GridOverrides, OrdinalDatePipe, RowSelection } from '@mds/common';
import { GwpChartComponent } from './component/gwp-chart/gwp-chart.component';
import { ScoreCompareComponent } from './component/score-compare/score-compare.component';
import { ToastrService } from '@services/toaster.service';
import { MenuService } from '@services/menu.service';
import { ScoreCompareTableComponent } from './component/score-compare-table/score-compare-table.component';
import { PefAnalysisService } from './services/pef-anlysis.service';
import { ColDef, ColGroupDef, GridApi } from 'ag-grid-community';
import { getEntityTypeIcon } from '@utils/entity-type-icon';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import { InviteShareComponent } from '@dialogs/share-invite/share-invite-dialog.component';
import { ProductSearchDialogComponent } from '@dialogs/product-search-dialog/product-search-dialog.component';

@Component({
  selector: 'app-pef-analysis',
  standalone: true,
  templateUrl: './pef-analysis.component.html',
  styleUrls: ['./pef-analysis.component.scss'],
  providers: [PefAnalysisService],
  imports: [
    ChipComponent,
    GwpChartComponent,
    MdsIconsModule,
    DropdownMenuComponent,
    ButtonComponent,
    GridModule,
    PefScoreComponent,
    ScoreCompareComponent,
    ScoreCompareTableComponent,
    LoaderModule,
    OrdinalDatePipe,
    AlertComponent
  ],
})
export class PefAnalysisComponent implements OnInit {
  scoreAgGrid: GridApi;
  selectedProducts: any = [];
  icons = MdsIcon;
  data: any;
  selectedPcr: any = {};
  rid: any;
  overrides: GridOverrides;
  footnoteButtonIcon = this.icons.KEYBOARD_ARROW_DOWN;
  isFootnoteCollapse = true;
  comparisonData: any;
  scenarioAgGrid: any;
  scoreLoading = false;
  loading = true;
  @ViewChild('footnoteButton') footnoteButton: ElementRef;
  isExporting = false;
  protected pcrList: any = [];
  private destroyRef = inject(DestroyRef);
  private toastrService = inject(ToastrService);
  private menuService = inject(MenuService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private composerApiService = inject(ComposerApiService);
  private dialog = inject(MatDialog);
  private pefAnalysisService = inject(PefAnalysisService);

  ngOnInit(): void {
    this.route.params
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((params: Params) => (this.rid = params.rid)),
        switchMap((params: Params) => this.composerApiService.getTileCards(params.rid))
      )
      .subscribe((results: any[]) => {
        const entity = results.length > 0 ? results[0] : [];
        this.menuService.setMenuConfig({
          activeApp: {
            mainIcon: {
              icon: MdsIcon.APP_PEF_ANALYSIS,
            },
            name: 'PEF Dashboard',
            icons: [],
            subtitle: '',
            menuTrigger: 'selectApps',
          },
          activeEntity: {
            mainIcon: {
              icon: MdsIcon.PRODUCT_GROUP,
              pathColors: ['#fff'],
            },
            rid: entity?.rid,
            name: entity?.title,
            icons: [{ key: getEntityTypeIcon(entity?.stage) }],
            subtitle: entity?.card_type_label,
          },
          rightActions: [
            {
              label: 'Edit',
              key: 'edit',
              icon: MdsIcon.EDIT,
              selected: false,
              disabled: false,
              visible: true,
            },
            {
              label: 'Export PDF',
              key: 'pdf',
              icon: MdsIcon.DOWNLOAD,
              selected: false,
              disabled: false,
              visible: true,
            },
            {
              label: 'Export Word',
              key: 'docx',
              icon: MdsIcon.FILE_DOWNLOAD,
              selected: false,
              disabled: false,
              visible: true,
            },
            {
              label: 'Share',
              key: 'share',
              icon: MdsIcon.SHARE,
              selected: false,
              disabled: false,
              visible: true,
            },
            {
              label: 'Invite',
              key: 'invite',
              icon: MdsIcon.INVITE,
              selected: false,
              disabled: false,
              visible: true,
            },
          ],
        });
        this.init();
      });

    this.menuService.reload$
      .pipe(
        filter((reload) => !!reload),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.init();
      });

    this.menuService.selectedAction$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((action) => {
      this.parseAllMenuEvent(action?.key);
    });
  }

  init() {
    this.loading = true;
    this.selectedPcr = {};
    this.comparisonData = null;
    this.pefAnalysisService
      .getPEF(this.rid)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (res) => {
          this.data = res;
          this.selectedPcr = this.data.impacts[0];
          this.pcrList = this.data.impacts.map((e, index) => ({
            rid: e.rid,
            text: e.pcr,
            info: e.pcr,
            disabled: false,
            selected: false,
            index,
          }));
          this.getComparisonData(this.selectedPcr.rid);
          this.getOverrides();
        },
        error: (err) => {
          this.toastrService.showMessage(err?.error?.message);
          this.router.navigate(['/dashboard']);
        },
      });
  }

  onChangePcr(ev: any) {
    this.selectedPcr = this.data.impacts[ev[0].index];
    this.getComparisonData(
      this.selectedPcr.rid,
      this.selectedProducts.map((pro) => pro.rid)
    );
    this.getOverrides();
  }

  getRowData(data: any): Record<string, any>[] {
    const rowData: Record<string, any>[] = [];
    let count = 1;
    data.forEach((element) => {
      rowData.push({ ...element, Nr: count });
      count++;
    });
    return rowData;
  }

  footnoteToggle() {
    this.isFootnoteCollapse = !this.isFootnoteCollapse;
    this.footnoteButtonIcon = this.isFootnoteCollapse ? this.icons.KEYBOARD_ARROW_DOWN : this.icons.KEYBOARD_ARROW_UP;
  }

  configureAlternatives(event: any) {
    const dialogRef = this.dialog.open(ProductSearchDialogComponent, {
      data: {
        multiple: true,
        selectOption: false,
        maxSelection: 5,
        type: 'pef-product-search',
        action: 'products-search',
        selectType: { label: 'Product Groups', value: 'product' },
        defaultLoadProduct: this.selectedProducts,
        headerText: 'Add Product Alternatives',
      },
      width: '52vw',
      height: '80vh',
    });
    dialogRef.afterClosed().subscribe(({ rids, tiles }) => {
      if (rids && tiles) {
        this.selectedProducts = tiles;
        this.getComparisonData(this.selectedPcr.rid, rids);
      }
    });
  }

  onGridReady(gridApi: GridApi) {
    this.scoreAgGrid = gridApi;
  }

  scenarioAgGridEmit(ev) {
    this.scenarioAgGrid = this.gridToHtmlTable(ev);
  }

  export(fileType: string) {
    this.isExporting = true;
    try {
      const scoreAgGrid = this.gridToHtmlTable(this.scoreAgGrid);
      this.footnoteButton?.nativeElement?.click();
      this.toastrService.showMessage('Please wait, It will take few seconds to Export.', 3000);
      const filename = `Makersite_PEF Analysis of ${this.data.name}`;
      if (fileType === 'pdf') this.pefAnalysisService.export('pefDashboard', filename, fileType);
      if (fileType === 'docx') {
        this.pefAnalysisService.export('pefDashboard', filename, fileType, scoreAgGrid, this.scenarioAgGrid);
        setTimeout(() => {
          this.footnoteButton?.nativeElement?.click();
        }, 8000);
      }
    } finally {
      setTimeout(() => {
        this.isExporting = false;
      }, 4000);
    }
  }

  extractLeafColDefs(columnDefs: (ColDef | ColGroupDef)[]): ColDef[] {
    const leafCols: ColDef[] = [];
    for (const col of columnDefs) {
      if ('children' in col && Array.isArray(col.children)) {
        leafCols.push(...this.extractLeafColDefs(col.children));
      } else if ('field' in col) {
        leafCols.push(col);
      }
    }
    return leafCols;
  }

  gridToHtmlTable(gridApi: GridApi): string {
    if (!gridApi) {
      return;
    }
    const rowModel = gridApi.getModel();
    if (!rowModel) return '';
    const rows: string[] = [];
    const headerCells: string[] = [];
    const rawColDefs = gridApi.getColumnDefs() || [];
    const leafColDefs = this.extractLeafColDefs(rawColDefs);
    for (const col of leafColDefs) {
      headerCells.push(`<th>${col.headerName ?? col.field}</th>`);
    }
    rows.push(`<tr>${headerCells.join('')}</tr>`);
    rowModel.forEachNode((node) => {
      const cellValues: string[] = [];
      for (const col of leafColDefs) {
        const value = col.field ? gridApi.getValue(col.field, node) : '';
        cellValues.push(`<td>${value ?? ''}</td>`);
      }
      rows.push(`<tr>${cellValues.join('')}</tr>`);
    });
    return `<table style="border-collapse:collapse" border="1">${rows.join(
      ''
    )}</table>`;
  }

  private getComparisonData(pcrRid: string, rids: string[] = []) {
    this.comparisonData = null;
    this.scoreLoading = true;
    pcrRid = pcrRid.replace('#', '');
    this.pefAnalysisService
      .getScoreComparison(this.rid, pcrRid, rids)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.scoreLoading = false))
      )
      .subscribe({
        next: (res) => {
          this.comparisonData = res;
        },
      });
  }


  private getOverrides() {
    const rowData = this.getRowData(this.selectedPcr.scores ?? []);
    const columnDefs = this.selectedPcr.scores[0] ? this.getColumns(this.selectedPcr.scores[0]) : [];
    this.overrides = {
      defaultColDef: {
        sortable: true,
        resizable: true,
        filter: true,
        enablePivot: true,
        enableRowGroup: true,
      },
      columnDefs,
      rowData,
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
          },
        ],
      },
      suppressRowClickSelection: true,
      rowSelection: RowSelection.MULTIPLE,
      noRowsOverlayComponent: GridNoRowsComponent,
      noRowsOverlayComponentParams: {
        title: 'No data to show the table',
      },
    };
  }

  private getColumns(data: any): any[] {
    const columnDefs = [];
    const keys = Object.keys(data);
    const impact = keys.find((key) => key === 'impact');
    const category = keys.find((key) => key === 'category');
    const percentage = keys.find((key) => key === 'percentage');
    const normalized = keys.find((key) => key === 'normalized');
    columnDefs.push({
      headerName: 'Nr',
      filter: '',
      floatingFilter: false,
      lockPosition: false,
      pinned: 'left',
      minWidth: 50,
      field: 'Nr',
    });

    if (category) {
      columnDefs.push({
        headerName: 'Impact Category',
        filter: 'agTextColumnFilter',
        floatingFilter: false,
        lockPosition: false,
        pinned: 'left',
        minWidth: 250,
        field: category,
      });
    }
    if (impact) {
      columnDefs.push({
        headerName: 'Impact/30g',
        filter: 'agTextColumnFilter',
        floatingFilter: false,
        lockPosition: false,
        pinned: 'left',
        minWidth: 100,
        field: impact,
        cellStyle: { 'text-align': 'right' },
      });
    }
    if (percentage) {
      columnDefs.push({
        headerName: 'Weighted & Normalized (%)',
        filter: 'agTextColumnFilter',
        floatingFilter: false,
        lockPosition: false,
        pinned: 'left',
        minWidth: 100,
        field: percentage,
        cellStyle: { 'text-align': 'right' },
      });
    }
    if (normalized) {
      columnDefs.push({
        headerName: 'Weighted & Normalized (pt)',
        filter: 'agTextColumnFilter',
        floatingFilter: false,
        lockPosition: false,
        pinned: 'left',
        minWidth: 100,
        field: normalized,
        cellStyle: { 'text-align': 'right' },
      });
    }
    return columnDefs;
  }

  private share() {
    const userInfo = JSON.parse(localStorage.getItem('user_info'));
    const link = `epd-dashboard/${this.rid}`;
    const data = {
      type: 'showInvite',
      value: 'Share',
      data: {
        fr: userInfo.email,
        link,
        rid: this.rid,
        usersLackingAccessList: '',
      },
    };

    this.dialog.open(InviteShareComponent, {
      data,
      panelClass: 'show-invite',
    });
  }

  private invite() {
    this.dialog.open(InviteShareComponent, {
      data: { type: 'showInvite', value: 'Invite' },
      panelClass: 'show-invite',
    });
  }

  private parseAllMenuEvent(key: string) {
    switch (key) {
      case 'edit':
        this.router.navigateByUrl(`/mattermaps/overview/${this.rid}/edit`);
        break;
      case 'share':
        this.share();
        break;
      case 'invite':
        this.invite();
        break;
      case 'pdf':
      case 'docx':
        this.export(key);
        break;
    }
  }
}
